import { useEffect, useState } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { sendTestSms } from "../lib/sms";
import { S, C } from "../ui";

const DEFAULT_CFG = { smsEnabled: false, phoneNumbers: [], clockInOutEnabled: false, missedVisitEnabled: false };

// Scoped per building (facilityId), not per owner — see functions/index.js's
// header comment on notificationSettings/{facilityId}. Ported from
// CustodialView's own Admin Setup → Notifications section; the consent
// disclosure text below is required for A2P 10DLC compliance, same wording
// pattern that got that campaign approved.
export default function AlertsPanel({ facilityId, facilityName }) {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [testMsg, setTestMsg] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");

  useEffect(() => onSnapshot(doc(db, "notificationSettings", facilityId), (s) =>
    setCfg(s.exists() ? { ...DEFAULT_CFG, ...s.data() } : DEFAULT_CFG)), [facilityId]);

  const save = async () => {
    const phoneNumbers = (cfg.phoneNumbers || []).map((p) => (p || "").trim())
      .filter((p) => p && p.replace(/^\+1\s*/, "").length > 0).slice(0, 2);
    await setDoc(doc(db, "notificationSettings", facilityId), { ...cfg, phoneNumbers }, { merge: true });
    setSaveMsg("Saved.");
    setTimeout(() => setSaveMsg(""), 2000);
  };

  const runTest = async () => {
    setTestBusy(true); setTestMsg("");
    try {
      const r = await sendTestSms(facilityId);
      setTestMsg(`✓ Sent to ${r.to.join(", ")}`);
    } catch (e) {
      setTestMsg(`✗ ${e.message}`);
    }
    setTestBusy(false);
  };

  return (
    <div style={{ maxWidth: 520 }}>
      <div style={{ background: C.white, borderRadius: 0, border: `1px solid ${C.border}`, padding: 20 }}>
        <div style={{ fontWeight: 700 }}>Text alerts for {facilityName}</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>Instant SMS to your phone — not a scheduled report. Optional — you can use Edifice View without opting in.</div>

        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Edifice View can text your phone about worker clock-in/clock-out activity and missed
          scheduled visits at this building. Review the disclosure below, then check the box to
          opt in — it's unchecked until you do.
        </div>

        {/* This checkbox IS the opt-in — unchecked by default, and its own label is the full
            consent disclosure, not implied by enabling some other setting or entering a phone
            number. Kept as a single affirmative action (not split from smsEnabled into a second
            field) so there's no way for the stored state to say "consented" without this exact
            box having been checked. */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 16 }}>
          <div
            style={{ width: 20, height: 20, flexShrink: 0, marginTop: 1, borderRadius: 0, border: `1.5px solid ${cfg.smsEnabled ? C.accent : C.border}`, background: cfg.smsEnabled ? C.accent : C.white, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: 13, cursor: "pointer" }}
            onClick={() => setCfg((f) => ({ ...f, smsEnabled: !f.smsEnabled }))}>
            {cfg.smsEnabled && "✓"}
          </div>
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, cursor: "pointer" }}
            onClick={() => setCfg((f) => ({ ...f, smsEnabled: !f.smsEnabled }))}>
            <strong style={{ color: C.ink }}>I agree to receive recurring automated operational SMS alerts from Edifice View</strong>{" "}
            regarding worker clock-ins, clock-outs, and missed scheduled visits. Message frequency
            varies. Message and data rates may apply. Reply STOP to opt out or HELP for help.
            Consent is optional. See our{" "}
            <a href="https://edificeview.com/terms" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: C.accent }}>Terms &amp; Conditions</a>{" "}
            and{" "}
            <a href="https://edificeview.com/privacy" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: C.accent }}>Privacy Policy</a>.
          </div>
        </div>

        {[0, 1].map((i) => (
          <input key={i} style={S.input} placeholder={`Phone number ${i + 1}${i === 1 ? " (optional)" : ""}`}
            value={(cfg.phoneNumbers || [])[i] ?? "+1"}
            onChange={(e) => setCfg((f) => {
              const phoneNumbers = [...(f.phoneNumbers || ["+1", "+1"])];
              phoneNumbers[i] = e.target.value;
              return { ...f, phoneNumbers };
            })} />
        ))}
        <div style={{ fontSize: 11, color: C.muted, marginTop: -10, marginBottom: 14 }}>
          Format: +1 followed by the 10-digit number, no dashes or spaces (e.g. +15125550123).
        </div>

        <div style={{ ...S.kicker, margin: "12px 0 8px" }}>Alert on</div>
        {[
          ["clockInOutEnabled", "Worker clock-in / clock-out"],
          ["missedVisitEnabled", "Missed scheduled visit"],
        ].map(([key, label]) => (
          <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <div
              style={{ width: 18, height: 18, borderRadius: 0, border: `1.5px solid ${cfg[key] ? C.accent : C.border}`, background: cfg[key] ? C.accent : C.white, display: "flex", alignItems: "center", justifyContent: "center", color: C.white, fontSize: 11, cursor: "pointer" }}
              onClick={() => setCfg((f) => ({ ...f, [key]: !f[key] }))}>
              {cfg[key] && "✓"}
            </div>
            <div style={{ fontSize: 13 }}>{label}</div>
          </div>
        ))}

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
          <button style={S.btn(C.green)} onClick={save}>Save</button>
          <button style={{ ...S.ghostBtn, width: "auto", padding: "10px 16px" }} disabled={testBusy} onClick={runTest}>
            {testBusy ? "Sending…" : "Send Test Text"}
          </button>
        </div>
        {saveMsg && <div style={{ fontSize: 12, color: C.accent, marginTop: 8 }}>{saveMsg}</div>}
        {testMsg && <div style={{ fontSize: 12, color: testMsg.startsWith("✓") ? C.accent : C.red, marginTop: 8 }}>{testMsg}</div>}
      </div>
    </div>
  );
}
